/**
 * Deploy detection: pick up a new build without asking anyone to hard refresh.
 *
 * A browser tab loads index.html once, boots a content-hashed bundle, and then
 * runs that bundle until something reloads the page. A deploy replaces the files
 * on the server and the open tab never notices. Two consequences, and the second
 * is the one that actually breaks:
 *
 *   1. New work stays invisible until someone refreshes, and nothing tells them.
 *   2. Lazily-loaded chunks 404. Their filenames are content-hashed, so a deploy
 *      that touched one removes the exact name the running tab is about to ask
 *      for. In the admin that is the PDF generator, the data-migration converter
 *      and the Jadwal calendar.
 *
 * Four parts answer both:
 *
 *   detect  scriptFingerprint() reads the build-emitted <script src> names out of
 *           a document. The booted one is captured when this module evaluates,
 *           the current one by re-fetching index.html.
 *   poll    every 5 minutes while the tab is visible, the moment it becomes
 *           visible again, and on a bfcache restore (which does NOT re-run this
 *           file, so a returning phone user would otherwise be invisible).
 *   apply   hardNavigateIfUpdated() swaps a client-side navigation for a real
 *           page load. Callers wire it into TOP-LEVEL navigation only, so the
 *           reload can never land mid-form: clicking the main menu already
 *           abandons whatever page you were on.
 *   heal    importChunk() catches a failed dynamic import and reloads once.
 *
 * Deliberately absent. No timer that reloads on its own, which would eventually
 * eat a half-filled form. No toast, because nobody should have to think about
 * which version of an app they are running. No service worker: that is a
 * request-interception layer with its own sticky failure modes, and we would be
 * adopting all of it for one event it happens to emit.
 *
 * The same module lives in all three front-ends. The two Vite apps carry it
 * byte-identically; the admin copy differs only in quote style.
 */

/** How often to re-check while the tab is in the foreground. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * A chunk-recovery reload is allowed at most once per this window.
 *
 * Not "once per session": an admin tab stays open across several deploys in a
 * day and has to be able to heal more than once. A genuine loop retries within
 * milliseconds, so a short window kills it without blocking the next deploy.
 */
const RELOAD_GUARD_MS = 30_000;

/**
 * sessionStorage, so it is per tab and cannot outlive the browsing session.
 *
 * New key, nothing to migrate, so it carries the current brand. The apps' older
 * localStorage prefixes deliberately still say terasku.
 */
const RELOAD_GUARD_KEY = "spott.reloadAt";

let bootedScripts = "";
let updateReady = false;
let pollTimer: number | undefined;
/** Fallback guard for when sessionStorage throws: one reload per page load. */
let reloadedThisPage = false;

/**
 * The build-emitted entry scripts of a document, as one comparable string.
 *
 * Only src values starting with "/" count, and that filter is load-bearing: the
 * admin's Firebase reCAPTCHA injects absolute https://www.gstatic.com/... script
 * tags into the live DOM at runtime, which would otherwise read as a new deploy
 * on the very first check.
 *
 * The entry script alone is a complete signal. A lazy chunk's hashed filename is
 * embedded in the entry's own chunk map, so changing any chunk changes the entry.
 */
const scriptFingerprint = (doc: Document): string =>
  Array.from(doc.querySelectorAll("script[src]"))
    .map((el) => el.getAttribute("src") ?? "")
    .filter((src) => src.startsWith("/"))
    .sort()
    .join("|");

const stopPolling = (): void => {
  if (pollTimer !== undefined) {
    window.clearInterval(pollTimer);
    pollTimer = undefined;
  }
};

const check = async (): Promise<void> => {
  if (updateReady || !bootedScripts) return;
  try {
    // The app shell, not /index.html: Railway's proxy 301s /index.html to / on
    // most of these services, and fetch would follow that for a second round
    // trip. cache: 'no-store' forces a real network fetch whatever the host
    // sends back as cache headers, which is what lets this work with no
    // serving config of our own.
    const res = await fetch("/", { cache: "no-store" });
    if (!res.ok) return;
    const served = scriptFingerprint(
      new DOMParser().parseFromString(await res.text(), "text/html"),
    );
    // An empty fingerprint means we did not get the app shell back: an error
    // page, a redirect, a captive portal. Reading that as "everything changed"
    // would reload on a loop, so it counts as no news.
    if (!served || served === bootedScripts) return;
    updateReady = true;
    stopPolling();
  } catch {
    // Offline or blocked. Try again on the next tick.
  }
};

const startPolling = (): void => {
  stopPolling();
  pollTimer = window.setInterval(() => void check(), POLL_INTERVAL_MS);
};

/** Reload unless we just did. Returns whether the reload actually fired. */
const reloadOnce = (): boolean => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? "0");
    if (Date.now() - last < RELOAD_GUARD_MS) return false;
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // Storage blocked (private mode, site data off). A timestamp cannot survive
    // the reload, so fall back to one attempt per page load.
    if (reloadedThisPage) return false;
  }
  reloadedThisPage = true;
  window.location.reload();
  return true;
};

const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const err = error as { name?: string; code?: string; message?: string };
  if (err.name === "ChunkLoadError") return true;
  if (err.code === "CSS_CHUNK_LOAD_FAILED") return true;
  const message = err.message ?? "";
  return (
    // webpack wording, then the native/Vite wording.
    /Loading (CSS )?chunk \S+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
};

/**
 * Load a lazy chunk, recovering from a filename the last deploy removed.
 *
 * On a chunk-load error it reloads the page and then returns a promise that
 * never settles, so the Suspense fallback or the button spinner stays put while
 * the browser tears the page down instead of flashing an error nobody can act
 * on. If the guard refused the reload it rethrows, and the caller's own catch
 * shows its normal message.
 */
export const importChunk = async <T>(factory: () => Promise<T>): Promise<T> => {
  try {
    return await factory();
  } catch (error) {
    if (!isChunkLoadError(error) || !reloadOnce()) throw error;
    return new Promise<T>(() => undefined);
  }
};

/**
 * Swap a client-side navigation for a real page load when a deploy is waiting.
 *
 * Returns true when it took over, so the caller returns instead of navigating.
 * Wire it into TOP-LEVEL navigation only. A main-menu click is structurally
 * never mid-flow, which is what makes this safe without inspecting any state:
 * there is no heuristic here that can be wrong about whether someone is busy.
 */
export const hardNavigateIfUpdated = (to: string): boolean => {
  if (!updateReady) return false;
  updateReady = false;
  window.location.href = to;
  return true;
};

/**
 * Start watching for deploys. Call once, at module scope, in production only.
 *
 * Nothing here touches React: the flag lives in this module, so provider
 * nesting, remounts and auth gates are all irrelevant to it.
 */
export const startVersionWatch = (): void => {
  // Backstop for any dynamic import that does not go through importChunk.
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) reloadOnce();
  });
  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.error)) reloadOnce();
  });

  // Both stacks defer the entry script (CRA writes <script defer>, Vite writes
  // type="module"), so by the time this module evaluates every build-emitted tag
  // is in the document and nothing has injected one of its own yet.
  bootedScripts = scriptFingerprint(document);
  if (!bootedScripts) return;

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") {
      stopPolling();
      return;
    }
    void check();
    startPolling();
  });

  // A bfcache restore resumes the frozen page without re-running startup code,
  // so it is the one moment a returning tab would otherwise go unchecked. Common
  // on mobile browsers: every tap back into the tab, every back from a link.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) void check();
  });

  if (document.visibilityState === "visible") startPolling();
};
